import React, { useEffect, useMemo, useRef } from 'react';
import {
  formatJGK,
  formatUsdJgkPairFromJgk,
  getLocalOpsConfig,
} from '../../utils/localOpsConfig';

const formatUsdFromJgk = (value, rate) => {
  const safeRate = Number.isFinite(rate) && rate > 0 ? rate : 1000;
  const usd = Number(value) / safeRate;
  if (!Number.isFinite(usd)) return '$0.00';
  return `$${usd.toFixed(2)}`;
};

export default function LocalOpsPayWithBalance({
  required = 0,
  balance = null,
  onTopUp,
  onTopUpComplete,
  label = 'Cost (JGK)',
  showTopUpButton = true,
  requiredLabelOverride = null,
  usdApproximate = true,
  amountDisplay = 'jgk_first',
}) {
  const localOpsConfig = useMemo(() => getLocalOpsConfig(), []);
  const rate = Number.isFinite(localOpsConfig?.jgkPerUsd)
    ? localOpsConfig.jgkPerUsd
    : 1000;
  const hasBalance = Number.isFinite(balance);
  const usdFirst = amountDisplay === 'usd_first';
  const formatAmount = (value) =>
    usdFirst ? formatUsdJgkPairFromJgk(value, rate) : formatJGK(value);
  const requiredLabel =
    usdFirst ? formatAmount(required) : requiredLabelOverride || formatJGK(required);
  const requiredUsdLabel = formatUsdFromJgk(required, rate);
  const balanceLabel = hasBalance ? formatAmount(balance) : 'Checking balance...';
  const postBalance = hasBalance ? Math.max(balance - required, 0) : null;
  const postBalanceLabel = hasBalance
    ? formatAmount(postBalance)
    : 'Checking balance...';
  const shortfall = hasBalance ? Math.max(required - balance, 0) : 0;
  const shortfallLabel = formatAmount(shortfall);
  const hasEnough = !hasBalance || balance >= required;
  const prevHasEnoughRef = useRef(hasEnough);
  const statusLabel = useMemo(() => {
    if (!hasBalance) return 'Checking balance';
    return hasEnough
      ? 'Ready to approve with your JGK Balance'
      : 'Top up to keep going';
  }, [hasBalance, hasEnough]);

  useEffect(() => {
    if (!prevHasEnoughRef.current && hasEnough) {
      onTopUpComplete?.();
    }
    prevHasEnoughRef.current = hasEnough;
  }, [hasEnough, onTopUpComplete]);

  return (
    <div className="localops-pay-card">
      <div className="localops-pay-title">Approve with your JGK Balance</div>
      <div className="localops-pay-row">
        <span>{label}</span>
        <span className="localops-pay-amount">
          {requiredLabel}
          {usdFirst ? null : (
            <span className="localops-pay-sub">
              ({usdApproximate ? '~' : ''}{requiredUsdLabel})
            </span>
          )}
        </span>
      </div>
      <div className="localops-pay-row">
        <span>Current balance</span>
        <span className="localops-pay-amount">{balanceLabel}</span>
      </div>
      <div className="localops-pay-row localops-pay-row--emphasis">
        <span>Balance after approval</span>
        <span className="localops-pay-amount">
          {postBalanceLabel}
          {!hasEnough && hasBalance ? (
            <span className="localops-pay-sub">
              (needs {shortfallLabel} more)
            </span>
          ) : null}
        </span>
      </div>
      <div className="localops-pay-status">{statusLabel}</div>
      <div className="localops-helper">
        JGK Balance is prepaid credit you can earn or top up.
      </div>
      {hasBalance ? (
        <div className="localops-helper">
          You'll have {postBalanceLabel} left after approval.
        </div>
      ) : null}
      {!hasEnough && hasBalance ? (
        <div className="localops-helper">
          You're short by {shortfallLabel}.
        </div>
      ) : null}
      {!hasEnough && showTopUpButton ? (
        <>
          <div className="localops-helper">
            Top up once and you're back here instantly.
          </div>
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={onTopUp}
          >
            Top up JGK Balance
          </button>
        </>
      ) : null}
    </div>
  );
}
